import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import SupportAgentRounded from "@mui/icons-material/SupportAgentRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";

/**
 * Tawk.to chat as a right-anchored side drawer.
 *
 * Uses the direct chat URL (`https://tawk.to/chat/<propertyId>/<widgetId>`)
 * which Tawk permits to be iframed — their hosted page is designed to be
 * embedded this way, so it works without needing the full floating-widget
 * script.
 *
 * Config reads from Vite env vars (public, bundled):
 *   VITE_TAWK_PROPERTY_ID
 *   VITE_TAWK_WIDGET_ID
 *
 * When either is missing we render a friendly "not configured" pane with a
 * link to the docs — the drawer still opens so the UX is demo-able.
 */

interface Props {
  open: boolean;
  onClose: () => void;
}

const PROPERTY_ID = import.meta.env.VITE_TAWK_PROPERTY_ID as string | undefined;
const WIDGET_ID   = import.meta.env.VITE_TAWK_WIDGET_ID as string | undefined;

const chatUrl =
  PROPERTY_ID && WIDGET_ID
    ? `https://tawk.to/chat/${PROPERTY_ID}/${WIDGET_ID}`
    : null;

export function TawkChatDrawer({ open, onClose }: Props) {
  const theme = useTheme();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Reset the loading state every time the drawer opens so the spinner
  // shows if the user closes and re-opens.
  useEffect(() => {
    if (open) setLoaded(false);
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          maxWidth: "100vw",
          display: "flex",
          flexDirection: "column",
          borderLeft: `1px solid ${theme.palette.divider}`,
          bgcolor: "background.default"
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: "primary.main",
            color: "primary.contrastText"
          }}
        >
          <SupportAgentRounded fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Live chat
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Real agents · usually reply in minutes
          </Typography>
        </Box>
        {chatUrl && (
          <Tooltip title="Open in new tab">
            <IconButton
              component="a"
              href={chatUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open chat in a new tab"
              size="small"
            >
              <OpenInNewRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Close">
          <IconButton onClick={onClose} aria-label="Close live chat" size="small">
            <CloseRounded fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Body — iframe or fallback */}
      <Box sx={{ flex: 1, minHeight: 0, position: "relative" }}>
        {chatUrl ? (
          <>
            {!loaded && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "background.default"
                }}
              >
                <Stack spacing={1.5} alignItems="center">
                  <CircularProgress size={28} />
                  <Typography variant="caption" color="text.secondary">
                    Connecting to support…
                  </Typography>
                </Stack>
              </Box>
            )}
            <iframe
              ref={iframeRef}
              src={chatUrl}
              title="Live chat with support"
              onLoad={() => setLoaded(true)}
              style={{
                width: "100%",
                height: "100%",
                border: 0,
                display: "block"
              }}
              // Tawk needs these permissions for file upload, clipboard, etc.
              allow="microphone; camera; clipboard-read; clipboard-write"
            />
          </>
        ) : (
          <NotConfiguredFallback />
        )}
      </Box>
    </Drawer>
  );
}

function NotConfiguredFallback() {
  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="center" sx={{ color: "warning.main" }}>
        <WarningAmberRounded />
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Live chat not configured
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        In-app live chat is powered by <strong>Tawk.to</strong>. To activate it on this
        build, set the following environment variables and redeploy:
      </Typography>

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          borderRadius: 2,
          bgcolor: "action.hover",
          fontFamily: '"JetBrains Mono", ui-monospace, monospace',
          fontSize: "0.8125rem",
          whiteSpace: "pre-wrap",
          wordBreak: "break-all"
        }}
      >
        {`VITE_TAWK_PROPERTY_ID=<your property id>
VITE_TAWK_WIDGET_ID=<your widget id>`}
      </Box>

      <Typography variant="body2" color="text.secondary">
        You can find these in your Tawk.to dashboard under{" "}
        <strong>Administration → Channels → Chat Widget</strong> — the "Direct Chat Link"
        embeds `/chat/PROPERTY/WIDGET`.
      </Typography>

      <Button
        variant="outlined"
        href="https://dashboard.tawk.to/"
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNewRounded fontSize="small" />}
      >
        Open Tawk.to dashboard
      </Button>
    </Box>
  );
}
